import React from 'react';
import { Link } from 'react-router-dom';
import { BookOpen, Trophy, Target, ArrowRight, Flame, Gift, Star } from 'lucide-react';

export default function ReadingChallengeWidget({ 
  activeChallenge, 
  currentBook, 
  readingStats, 
  isLoading = false, 
  isMobile = false 
}) {
  
  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl p-5 border border-stone-200 shadow-sm border-l-4 border-l-violet-500">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
            <BookOpen size={18} />
            Reading Challenge
          </h3>
        </div>
        <div className="text-center py-6">
          <BookOpen className="w-8 h-8 text-violet-300 animate-pulse mx-auto mb-2" />
          <p className="text-sm text-stone-500">Loading...</p>
        </div>
      </div>
    );
  }

  // No active challenge - encourage starting one
  if (!activeChallenge) {
    return (
      <div className="bg-white rounded-2xl p-5 border border-stone-200 shadow-sm border-l-4 border-l-violet-500">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
            <BookOpen size={18} />
            Reading Challenge
          </h3>
          <Link to="/reading" className="text-stone-500 hover:text-slate-900 font-semibold text-sm transition-colors">
            Start <ArrowRight size={16} className="inline ml-1" />
          </Link>
        </div>
        
        <div className="text-center py-6">
          <div className="w-12 h-12 bg-gradient-to-br from-violet-500 to-azure-600 rounded-xl flex items-center justify-center mx-auto mb-3">
            <Trophy className="w-6 h-6 text-white" />
          </div>
          <h4 className="font-semibold text-slate-900 mb-2">Start a Challenge!</h4>
          <p className="text-sm text-stone-600 mb-4">Set reading goals and earn rewards</p>
          <Link 
            to="/reading" 
            className="inline-flex items-center gap-2 px-4 py-2 bg-violet-500 text-white rounded-lg hover:bg-violet-600 transition-colors text-sm font-medium"
          >
            <Target className="w-4 h-4" />
            Begin Challenge
          </Link>
        </div>
      </div>
    );
  }

  // Active challenge - show progress
  const getDaysRemaining = () => {
    if (!activeChallenge?.end_date) return 0;
    const endDate = new Date(activeChallenge.end_date);
    const today = new Date();
    const diffTime = endDate - today;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  const getProgressPercentage = () => {
    if (activeChallenge?.challenge_type === 'book_count') {
      return Math.min(100, (activeChallenge.current_books / activeChallenge.target_books) * 100);
    } else {
      return Math.min(100, (activeChallenge.current_pages / activeChallenge.target_pages) * 100);
    }
  };

  const progressPercentage = getProgressPercentage();
  const daysRemaining = getDaysRemaining();
  const isBookChallenge = activeChallenge?.challenge_type === 'book_count';

  return (
    <div className="bg-white rounded-2xl p-5 border border-stone-200 shadow-sm border-l-4 border-l-violet-500">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
          <BookOpen size={18} />
          Reading Challenge
          {readingStats?.currentStreak > 0 && (
            <span className="flex items-center gap-1 bg-orange-100 text-orange-700 px-2 py-0.5 rounded-full text-xs font-semibold">
              <Flame size={12} />
              {readingStats.currentStreak}
            </span>
          )}
        </h3>
        <Link to="/reading" className="text-stone-500 hover:text-slate-900 font-semibold text-sm transition-colors">
          View <ArrowRight size={16} className="inline ml-1" />
        </Link>
      </div>

      {/* Challenge Progress */}
      <div className="mb-4">
        <div className="flex items-center justify-between mb-2">
          <h4 className="font-semibold text-slate-900 text-sm">
            {activeChallenge.challenge_name || `${activeChallenge.target_books || activeChallenge.target_pages} ${isBookChallenge ? 'Books' : 'Pages'} Challenge`}
          </h4>
          <span className="text-xs text-violet-600 font-semibold">{Math.round(progressPercentage)}%</span>
        </div>
        
        <div className="w-full bg-violet-100 rounded-full h-2 mb-2">
          <div 
            className="bg-gradient-to-r from-violet-500 to-azure-600 h-2 rounded-full transition-all duration-500"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
        
        <div className="flex items-center justify-between text-xs text-stone-600">
          <span>
            {isBookChallenge ? activeChallenge.current_books : activeChallenge.current_pages} of{' '}
            {isBookChallenge ? activeChallenge.target_books : activeChallenge.target_pages}{' '}
            {isBookChallenge ? 'books' : 'pages'}
          </span>
          <span>{daysRemaining} days left</span>
        </div>
      </div>

      {/* Current Book */}
      {currentBook && (
        <div className="bg-violet-50 rounded-lg p-3 mb-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-10 bg-gradient-to-br from-violet-500 to-azure-600 rounded flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
              {currentBook.book_title?.charAt(0) || 'B'}
            </div>
            <div className="flex-1 min-w-0">
              <h5 className="font-medium text-slate-900 text-sm truncate">
                {currentBook.book_title}
              </h5>
              <p className="text-xs text-violet-600">
                Page {currentBook.current_page}
                {currentBook.total_pages && ` of ${currentBook.total_pages}`}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Quick Stats */}
      <div className="grid grid-cols-2 gap-3 mb-4">
        <div className="bg-emerald-50 rounded-lg p-2 text-center">
          <div className="text-lg font-bold text-emerald-600">
            ${activeChallenge.reward_amount?.toFixed(2) || '0.00'}
          </div>
          <div className="text-xs text-emerald-700">Reward</div>
        </div>
        <div className="bg-azure-50 rounded-lg p-2 text-center">
          <div className="text-lg font-bold text-azure-600">
            ${readingStats?.totalEarned?.toFixed(2) || '0.00'}
          </div>
          <div className="text-xs text-azure-700">Earned</div>
        </div>
      </div>

      {/* Action Button */}
      <Link 
        to="/reading" 
        className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-gradient-to-r from-violet-500 to-azure-600 text-white rounded-lg hover:from-violet-600 hover:to-azure-700 transition-all text-sm font-medium"
      >
        <Trophy className="w-4 h-4" />
        {currentBook ? 'Continue Reading' : 'Start Reading'}
      </Link>
    </div>
  );
}